import React from 'react';
import EditableCardContent from './EditableCardContent.jsx';

/**
 * Unified Review Queue for AppHome (FlashFonic, FlashFoto, FlashClinical).
 */
const AppHomeReviewQueue = ({
    homeQueued,
    selectedQueue,
    onSelectedQueueChange,
    folders,
    targetFolderId,
    onTargetFolderChange,
    isMovingQueueCards,
    onMoveSelected,
    editingCardId,
    onEditingCardIdChange,
    pendingDeleteId,
    onPendingDeleteIdChange,
    onUpdateCard,
    onRemoveCard,
    onClearQueue,
    showClear = false,
    maxHeightClass = 'max-h-[60vh]',
}) => {
    const toggleSelectAll = () => {
        const allSelected = homeQueued.every((q) => selectedQueue[q.id]);
        if (allSelected) {
            onSelectedQueueChange({});
        } else {
            const next = {};
            homeQueued.forEach((q) => {
                next[q.id] = true;
            });
            onSelectedQueueChange(next);
        }
    };

    const canMove =
        homeQueued.length > 0 && Object.values(selectedQueue).some(Boolean) && !!targetFolderId;

    return (
        <div className="rounded-2xl bg-slate-900/60 border border-white/10 p-4 sm:p-5 shadow-lg shadow-black/20">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="text-base font-semibold tracking-tight text-white">Review Queue</h4>
                    {homeQueued.length > 0 && (
                        <button
                            type="button"
                            className="text-[11px] rounded-lg px-2 py-1 text-slate-200 bg-slate-800/70 ring-1 ring-white/10 hover:bg-slate-800"
                            onClick={toggleSelectAll}
                        >
                            {homeQueued.every((q) => selectedQueue[q.id]) ? 'Deselect all' : 'Select all'}
                        </button>
                    )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <select
                        className="rounded-xl bg-slate-800/70 border border-white/10 px-3.5 py-2 text-xs text-slate-200"
                        value={targetFolderId}
                        onChange={(e) => onTargetFolderChange(e.target.value)}
                        disabled={isMovingQueueCards}
                    >
                        <option value="">Move to folder…</option>
                        {folders.map((f) => (
                            <option key={f.id} value={f.id}>
                                {f.name}
                            </option>
                        ))}
                    </select>
                    <button
                        type="button"
                        className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs text-white bg-gradient-to-r from-teal-600 to-emerald-600 disabled:opacity-60"
                        onClick={onMoveSelected}
                        disabled={!canMove || isMovingQueueCards}
                    >
                        {isMovingQueueCards ? 'Moving...' : 'Move selected'}
                    </button>
                    {showClear && homeQueued.length > 0 && (
                        <button
                            type="button"
                            className="text-xs text-slate-300 hover:text-white"
                            onClick={onClearQueue}
                        >
                            Clear
                        </button>
                    )}
                </div>
            </div>
            <p className="mt-2 text-xs text-slate-400">Generate cards, then move them into a folder for safekeeping.</p>
            <div className="mt-3">
                {homeQueued.length > 0 && (
                    <div className="mb-2 flex items-center justify-end">
                        <div className="text-[11px] text-slate-400">
                            Selected: {Object.values(selectedQueue).filter(Boolean).length}
                        </div>
                    </div>
                )}
                <div className={`space-y-3 ${maxHeightClass} overflow-auto pr-1`}>
                    {homeQueued.length === 0 ? (
                        <div className="rounded-xl bg-slate-900/40 ring-1 ring-white/5 p-3 text-sm text-slate-400">
                            No cards yet. Generate from Live, Upload, FlashFoto, or FlashClinical.
                        </div>
                    ) : (
                        homeQueued.map((c, idx) => (
                            <div key={c.id} className="rounded-xl bg-slate-900/70 ring-1 ring-white/10 p-3">
                                <div className="flex items-start gap-3">
                                    <input
                                        type="checkbox"
                                        className="mt-0.5"
                                        checked={!!selectedQueue[c.id]}
                                        onChange={(e) =>
                                            onSelectedQueueChange((prev) => ({
                                                ...prev,
                                                [c.id]: e.target.checked,
                                            }))
                                        }
                                    />
                                    <div className="flex-1 min-w-0">
                                        {editingCardId === c.id ? (
                                            <EditableCardContent
                                                card={c}
                                                onSave={(updated) => {
                                                    onUpdateCard(c.id, updated);
                                                    onEditingCardIdChange(null);
                                                }}
                                                onCancel={() => onEditingCardIdChange(null)}
                                            />
                                        ) : (
                                            <>
                                                <p className="text-slate-200 break-words">
                                                    <strong>Q{idx + 1}:</strong>{' '}
                                                    {typeof c.question === 'string'
                                                        ? c.question
                                                        : JSON.stringify(c.question)}
                                                </p>
                                                <p className="text-slate-300 mt-1 break-words">
                                                    <strong>A:</strong>{' '}
                                                    {typeof c.answer === 'string'
                                                        ? c.answer
                                                        : JSON.stringify(c.answer)}
                                                </p>
                                                <div className="mt-3 flex flex-wrap gap-2">
                                                    <button
                                                        type="button"
                                                        className="edit-btn"
                                                        onClick={() => onEditingCardIdChange(c.id)}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="edit-btn"
                                                        onClick={() => onPendingDeleteIdChange(c.id)}
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                                {pendingDeleteId === c.id && (
                                                    <div className="mt-2 inline-flex items-center gap-2 rounded-lg bg-slate-800/70 px-3 py-1 text-xs text-slate-200">
                                                        <span>Delete this card?</span>
                                                        <button
                                                            type="button"
                                                            onClick={() => onRemoveCard(c.id)}
                                                            className="edit-btn"
                                                        >
                                                            Yes
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => onPendingDeleteIdChange(null)}
                                                            className="edit-btn"
                                                        >
                                                            No
                                                        </button>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default AppHomeReviewQueue;
